import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/react';
import { IconAlertCircleFilled } from '@tabler/icons-react';

import { getCalendars } from '../../api';
import { sortDataWithDirection } from 'common/utils';
import Loader from '../loader/Loader';
import StepNavBottom from './StepNavBottom';
import CalendarItemsTable from '../calendars/CalendarItemsTable';

const ContentCalendarStep = ({ onPrev, onNext }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [calendars, setCalendars] = useState([]);

  const hasApprovedCalendar = calendars.some((calendar) => calendar.status === 'approved');

  const fetchCalendars = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getCalendars();
      setCalendars(sortDataWithDirection(data || [], 'createdAt', 'desc'));
    } catch (err) {
      Sentry.captureException(err);
      setError(err?.message || 'Failed to load calendars');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchCalendars();
  }, [fetchCalendars]);

  if (loading) {
    return (
      <div className="relative flex h-96 max-w-4xl items-center justify-center">
        <Loader />
      </div>
    );
  }

  return (
    <div className="m-auto max-w-4xl space-y-12 pb-[60px]">
      <div className="space-y-4">
        <h1 className="text-h1 font-semibold">Content Calendar</h1>
        <p className="text-lg">
          Review your content calendar to see your upcoming email campaigns and scheduled communications.
        </p>
      </div>

      {calendars.length > 0 ? (
        <div className="divide-y divide-gray-100">
          {calendars.map((calendar) => (
            <div key={calendar.calendarId} className="py-8 first:pt-0">
              <CalendarItemsTable calendar={calendar} />
            </div>
          ))}
        </div>
      ) : (
        <div className="flex w-full justify-center">
          <div className="flex max-w-96 items-center gap-3 rounded-lg bg-primary-50 px-3 py-2">
            <IconAlertCircleFilled size={20} className="inline-block shrink-0 text-primary-700" />
            <p className="whitespace-pre-wrap text-base">
              The DonorSpring team is building out your Content Calendar. We will notify you when it is ready for your
              review.
            </p>
          </div>
        </div>
      )}

      {error && (
        <div className="flex w-full justify-center">
          <div className="flex max-w-96 items-center gap-3 rounded-lg bg-red-50 px-3 py-2">
            <IconAlertCircleFilled size={20} className="inline-block shrink-0 text-red-700" />
            <p className="whitespace-pre-wrap text-base text-red-800">{error}</p>
          </div>
        </div>
      )}

      <StepNavBottom onPrev={onPrev} onNext={onNext} nextDisabled={!hasApprovedCalendar} />
    </div>
  );
};

ContentCalendarStep.propTypes = {
  onPrev: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
};

export default ContentCalendarStep;
